import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { paymentModule } from "../api/modules/payment.module";

export const bankDetailsKeys = {
  all: ["bankDetails"],
  lists: () => [...bankDetailsKeys.all, "list"],
  list: (params) => [...bankDetailsKeys.lists(), params],
};

export const useBankDetails = (options = {}) => {
  return useQuery({
    queryKey: bankDetailsKeys.lists(),
    queryFn: async () => {
      console.log("useBankDetails: Starting API call");
      const response = await paymentModule.listBankDetails();
      console.log("useBankDetails: API response:", response);
      
      if (!response.success) {
        console.error("useBankDetails: API call failed:", response.message);
        throw new Error(response.message);
      }
      
      let bankDetails = response.data;
      console.log("useBankDetails: Processing data:", bankDetails);
      
      if (bankDetails && typeof bankDetails === 'object' && !Array.isArray(bankDetails)) {
        if (bankDetails.id || bankDetails.account_holder_name || bankDetails.account_number) {
          console.log("useBankDetails: Converting single object to array");
          bankDetails = [bankDetails];
        }
      }
      
      if (bankDetails && typeof bankDetails === 'object' && bankDetails.bank_accounts) {
        console.log("useBankDetails: Extracting bank_accounts array");
        bankDetails = bankDetails.bank_accounts;
      }
      
      if (bankDetails && typeof bankDetails === 'object' && bankDetails.data && Array.isArray(bankDetails.data)) {
        console.log("useBankDetails: Extracting nested data array");
        bankDetails = bankDetails.data;
      }
      
      const result = Array.isArray(bankDetails) ? bankDetails : [];
      // Normalize default flag for UI
      const normalized = result.map((item) => ({
        ...item,
        is_default: item?.is_default ?? item?.is_primary ?? false,
      }));
      console.log("useBankDetails: Final result:", normalized);
      return normalized;
    },
    staleTime: 5 * 60 * 1000, 
    retry: 2,
    ...options,
  });
};

export const useAddBankDetails = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload) => {
      const response = await paymentModule.addBankDetails(payload);
      if (!response.success) {
        throw new Error(response.message);
      }
      return response.data;
    },
    onSuccess: (data, variables) => {
      // If the new payment method is set as primary, update the cache optimistically
      if (variables.is_primary) {
        queryClient.setQueryData(bankDetailsKeys.lists(), (oldData) => {
          if (!oldData || !Array.isArray(oldData)) return oldData;
          
          // Remove primary status from all existing payment methods
          return oldData.map(item => ({
            ...item,
            is_default: false,
            is_primary: false
          }));
        });
      }
      
      queryClient.invalidateQueries({ queryKey: bankDetailsKeys.all });
      queryClient.invalidateQueries({ queryKey: bankDetailsKeys.lists() });
    },
    onError: (error) => {
      console.error("Failed to add bank details:", error);
    },
  });
};

export const useUpdateBankDetails = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload) => {
      const response = await paymentModule.updateBankDetails(payload);
      if (!response.success) {
        throw new Error(response.message);
      }
      return response.data;
    },
    onSuccess: (data, variables) => {
      // If this payment method is set as primary, update all others to not be primary
      if (variables.is_primary) {
        queryClient.setQueryData(bankDetailsKeys.lists(), (oldData) => {
          if (!oldData || !Array.isArray(oldData)) return oldData;
          
          return oldData.map(item => {
            const isCurrentItem = item.id === variables.bank_account_id || 
                                 item.bank_account_id === variables.bank_account_id;
            
            return {
              ...item,
              is_default: isCurrentItem ? true : false,
              is_primary: isCurrentItem ? true : false
            };
          });
        });
      } else {
        // Normal update without affecting primary status
        queryClient.setQueryData(bankDetailsKeys.lists(), (oldData) => {
          if (!oldData || !Array.isArray(oldData)) return oldData;
          
          return oldData.map(item => 
            (item.id === variables.bank_account_id || item.bank_account_id === variables.bank_account_id) 
              ? { ...item, ...variables, is_default: variables?.is_default ?? variables?.is_primary ?? item?.is_default }
              : item
          );
        });
      }
      
      queryClient.invalidateQueries({ queryKey: bankDetailsKeys.all });
      queryClient.invalidateQueries({ queryKey: bankDetailsKeys.lists() });
    },
    onError: (error) => {
      console.error("Failed to update bank details:", error);
    },
  });
};

export const useDeleteBankDetails = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (bankAccountId) => {
      const response = await paymentModule.deleteBankDetails(bankAccountId);
      if (!response.success) {
        throw new Error(response.message);
      }
      return response.data;
    },
    onMutate: async (bankAccountId) => {
      await queryClient.cancelQueries({ queryKey: bankDetailsKeys.lists() });

      const previousBankDetails = queryClient.getQueryData(bankDetailsKeys.lists());

      queryClient.setQueryData(bankDetailsKeys.lists(), (oldData) => {
        if (!oldData || !Array.isArray(oldData)) return oldData;
        return oldData.filter(item => 
          item.id !== bankAccountId && item.bank_account_id !== bankAccountId
        );
      });

      return { previousBankDetails };
    },
    onError: (err, bankAccountId, context) => {
      if (context?.previousBankDetails) {
        queryClient.setQueryData(bankDetailsKeys.lists(), context.previousBankDetails);
      }
      console.error("Failed to delete bank details:", err);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: bankDetailsKeys.all });
      queryClient.invalidateQueries({ queryKey: bankDetailsKeys.lists() });
    },
  });
};